import { useForm, type UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { normalizePersonalName } from '@/lib/normalize-personal-name';

/*
 * Form schema for the public registration flow.
 *
 * The shape mirrors the BE `POST /api/public/intermediary-onboarding/request`
 * body so submit-time mapping is mechanical (just attach the Turnstile
 * token + derive agreementType + flatten the address slots into the BE
 * `addresses[]` array).
 *
 * PR-A.1 contract hardening (BE) → adminUser identity required:
 *   sex, dateOfBirth, birthCountry; cadastral if birthCountry === 'IT'.
 *
 * PR-B (FE) → schema + step routing scaffolding for the new 6-step flow.
 *
 * PR-C (FE — this file) → multi-address slots (sede legale + operativa +
 * corrispondenza for COMPANY; residenza + domicilio for PERSON) and
 * `orgContacts` (email + pec required, mobile + landline optional).
 *
 * PERSON identity coupling (BE — superRefine): when legalNature === PERSON,
 *   - normalize(organization.legalName) === normalize(adminUser.fullName)
 *   - organization.fiscalCode === adminUser.fiscalCode (case-insensitive)
 *
 * Date inputs are kept as `YYYY-MM-DD` strings (HTML5 native pickers);
 * conversion to ISO 8601 datetime happens at the API boundary
 * (`lib/submit-payload.ts`).
 */

const ORG_TYPE_VALUES = ['BROKER', 'AGENZIA', 'INTERMEDIARIO'] as const;
const LEGAL_NATURE_VALUES = ['PERSON', 'COMPANY'] as const;
export const SEX_VALUES = ['M', 'F'] as const;
export type Sex = (typeof SEX_VALUES)[number];

// Withholding regime mirror of `WITHHOLDING_TYPE_VALUES` on the BE
// (`IntermediaryOnboardingService.ts`). PERSON locked on
// PRESTAZIONE_OCCASIONALE_20; COMPANY picks one of the other three.
export const WITHHOLDING_TYPE_VALUES = [
  'ORDINARIA_23_50',
  'RIDOTTA_23_20',
  'FORFETTARIO_0',
  'PRESTAZIONE_OCCASIONALE_20',
] as const;
export type WithholdingType = (typeof WITHHOLDING_TYPE_VALUES)[number];

export const COMPANY_WITHHOLDING_TYPE_VALUES = [
  'ORDINARIA_23_50',
  'RIDOTTA_23_20',
  'FORFETTARIO_0',
] as const;

const FISCAL_CODE_REGEX = /^[A-Z0-9]{11,16}$/;
const VAT_NUMBER_REGEX = /^[0-9]{11}$/;
// RUI number — the FE collects ONLY the 9 numeric digits (the section letter
// A/B/E is derived from org_type and prepended to the BE-canonical number at
// submit; PR-ONBOARDING-RUI-LOOKUP-UX-INPUT-1). The BE remains authority on
// canonicalisation + section coherence (PR-ONBOARDING-IVASS-RUI-ENFORCE-1).
const RUI_DIGITS_REGEX = /^[0-9]{9}$/;
const ISO_COUNTRY_REGEX = /^[A-Z]{2}$/;
const CADASTRAL_CODE_REGEX = /^[A-Z][0-9A-Z]{3}$/;
// Loose phone-number shape: digits + spaces + dashes + optional leading
// '0'. The PhoneCountryPicker carries the dial prefix separately so we
// only need to constrain the local part here. BE just persists the
// concatenated string; no E.164 enforcement in the public form.
const LOCAL_PHONE_REGEX = /^[\d\s\-./()]{4,30}$/;

const DateStringSchema = z
  .string()
  .refine(
    (v) => v === '' || /^\d{4}-\d{2}-\d{2}$/.test(v),
    'Formato data non valido',
  );

const RequiredDateStringSchema = z
  .string()
  .refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v), 'Data richiesta');

// ── Phone-with-prefix entry ─────────────────────────────────────────────────
//
// Decomposed: { prefixIso, number }.
//   prefixIso  ISO 3166-1 alpha-2 of the country whose dial code applies
//              (e.g. "IT" → +39). Source of truth for PhoneCountryPicker.
//   number     Local part typed by the user (digits + separators).
//
// Cross-field rules in the form root drive required-ness per
// legalNature × slot. Empty `number` means "not provided" — the submit
// mapper drops the channel rather than emitting "+39 ".
const PhoneEntrySchema = z.object({
  prefixIso: z.string().regex(ISO_COUNTRY_REGEX),
  number: z
    .string()
    .refine(
      (v) => v === '' || LOCAL_PHONE_REGEX.test(v),
      'Numero non valido (4-30 cifre/spazi/separatori)',
    ),
});

export type PhoneEntry = z.infer<typeof PhoneEntrySchema>;

const OrganizationSchema = z
  .object({
    legalNature: z.enum(LEGAL_NATURE_VALUES),
    legalName: z.string().min(1, 'Campo obbligatorio').max(200),
    orgType: z.enum(ORG_TYPE_VALUES),
    fiscalCode: z
      .string()
      .refine(
        (v) => v === '' || FISCAL_CODE_REGEX.test(v),
        'Codice fiscale non valido (11–16 caratteri alfanumerici)',
      )
      .optional(),
    vatNumber: z
      .string()
      .refine(
        (v) => v === '' || VAT_NUMBER_REGEX.test(v),
        'P.IVA non valida (11 cifre)',
      )
      .optional(),
    ruiNumber: z
      .string()
      .max(40)
      .refine(
        (v) =>
          v === undefined ||
          v.trim() === '' ||
          RUI_DIGITS_REGEX.test(v.trim()),
        'Il numero RUI deve contenere esattamente 9 cifre.',
      )
      .optional(),
    ruiRegistrationDate: DateStringSchema.optional(),
    /**
     * Codice SDI — fatturazione elettronica. Optional field, mostly
     * pre-filled by the company-lookup response (PR-OpenAPI-2). The
     * applicant can also type it manually. BE contract:
     * `organizations.sdi_code` (length 1..20, optional).
     */
    sdiCode: z.string().max(20).optional(),
    /**
     * Forma giuridica — closed list. Required for COMPANY (cross-field
     * rule below); empty string allowed for PERSON (non si applica).
     * Stored as plain text on the BE (`organizations.business_legal_form`).
     */
    businessLegalForm: z.string().max(200),
    /**
     * Withholding regime — PR-D. Empty string allowed at the schema
     * level (carried by DEFAULT_VALUES); the OrganizationSchema
     * superRefine below enforces non-empty + per-legalNature value
     * rules. We DO NOT use `.default('')` because zod's default on a
     * required field produces an input/output type mismatch under
     * react-hook-form's `Resolver<T>` generic.
     */
    withholdingType: z.union([
      z.enum(WITHHOLDING_TYPE_VALUES),
      z.literal(''),
    ]),
  })
  .superRefine((data, ctx) => {
    const cf = data.fiscalCode?.trim() ?? '';
    const vat = data.vatNumber?.trim() ?? '';
    // RUI number is mandatory for every intermediary org_type (AGENZIA /
    // BROKER / INTERMEDIARIO → sezioni A/B/E). The section itself is derived
    // BE-side; here we only enforce presence (PR-ONBOARDING-IVASS-DOCS-PER-TYPE-FE-1).
    if ((data.ruiNumber?.trim() ?? '').length === 0) {
      ctx.addIssue({
        code: 'custom',
        path: ['ruiNumber'],
        message: 'Numero RUI obbligatorio per gli intermediari iscritti al RUI.',
      });
    }
    if (data.legalNature === 'PERSON') {
      if (cf.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['fiscalCode'],
          message: 'Codice fiscale richiesto per persona fisica',
        });
      }
      if (vat.length > 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['vatNumber'],
          message: 'P.IVA non applicabile a persona fisica',
        });
      }
      // Withholding lock for PERSON. PRESTAZIONE_OCCASIONALE_20 is the
      // only legal value; the FiscalePage auto-sets it on mount.
      if (data.withholdingType !== 'PRESTAZIONE_OCCASIONALE_20') {
        ctx.addIssue({
          code: 'custom',
          path: ['withholdingType'],
          message:
            'Persona fisica: ritenuta locked a Prestazione occasionale 20%',
        });
      }
    } else {
      if (vat.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['vatNumber'],
          message: 'P.IVA richiesta per società',
        });
      }
      // Codice fiscale obbligatorio anche per le società/enti
      // (PR-ONBOARDING-PUBLIC-SMOKE-FIX-1). Spesso coincide con la P.IVA, ma
      // resta un identificativo distinto e va salvato esplicitamente.
      if (cf.length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['fiscalCode'],
          message: 'Codice fiscale richiesto per società',
        });
      }
      // Forma giuridica required per società.
      if (data.businessLegalForm.trim().length === 0) {
        ctx.addIssue({
          code: 'custom',
          path: ['businessLegalForm'],
          message: 'Tipo impresa richiesto per società',
        });
      }
      // Withholding for COMPANY: must be one of the 3 ordinary regimes.
      if (
        data.withholdingType === '' ||
        data.withholdingType === 'PRESTAZIONE_OCCASIONALE_20'
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['withholdingType'],
          message: 'Seleziona il regime di ritenuta d\'acconto',
        });
      }
    }
  });

const AdminSchema = z
  .object({
    fullName: z.string().min(1, 'Campo obbligatorio').max(200),
    email: z.string().email('Email non valida'),
    fiscalCode: z
      .string()
      .regex(FISCAL_CODE_REGEX, 'Codice fiscale non valido (11–16 caratteri alfanumerici)'),
    sex: z.enum(SEX_VALUES, { message: 'Sesso richiesto' }),
    dateOfBirth: RequiredDateStringSchema,
    birthCountry: z
      .string()
      .regex(ISO_COUNTRY_REGEX, 'Paese di nascita richiesto'),
    birthMunicipalityCadastralCode: z
      .string()
      .refine(
        (v) => v === '' || CADASTRAL_CODE_REGEX.test(v),
        'Codice catastale non valido (1 lettera + 3 alfanumerici)',
      )
      .optional(),
    placeOfBirth: z.string().max(120).optional(),
    /**
     * Mobile phone of the admin / signatory. Required for COMPANY (the
     * signatory has their own cellulare separate from the org line).
     * Optional for PERSON (the org cellulare in `orgContacts.mobile`
     * already covers the same person). Cross-field rule lives at the
     * form root.
     */
    mobilePhone: PhoneEntrySchema,
    /**
     * Personal RUI of the responsible person — required for AGENZIA/BROKER
     * COMPANY (cross-field rule at the form root). Section is derived by the BE
     * from org_type (not entered here). PR-ONBOARDING-COMPANY-RESPONSIBLE-RUI-1.
     */
    ruiNumber: z
      .string()
      .max(40)
      .refine(
        (v) =>
          v === undefined ||
          v.trim() === '' ||
          RUI_DIGITS_REGEX.test(v.trim()),
        'Il numero RUI deve contenere esattamente 9 cifre.',
      )
      .optional(),
    ruiRegistrationDate: z.string().optional(),
  })
  .superRefine((data, ctx) => {
    if (
      data.birthCountry === 'IT' &&
      (!data.birthMunicipalityCadastralCode ||
        data.birthMunicipalityCadastralCode.trim() === '')
    ) {
      ctx.addIssue({
        code: 'custom',
        path: ['birthMunicipalityCadastralCode'],
        message:
          'Comune di nascita richiesto per nascita in Italia',
      });
    }
  });

// ── Multi-address slots (PR-C) ──────────────────────────────────────────────
//
// We hold every potential slot in the form state at all times so the
// controlled inputs do not bounce between mounted/unmounted. Required-ness
// is driven by `enabled` flags + `legalNature`, not by Zod object presence.
// Cross-field validation lives at the form root (`superRefine` on
// `RegistrationFormSchema` below).
//
// Mapping to BE `addresses[]` happens at submit time in
// `lib/submit-payload.ts`:
//   COMPANY → primary=REGISTERED_OFFICE, alternateOffice=OPERATIVE_OFFICE,
//             mailingAddress=MAILING
//   PERSON  → primary=RESIDENCE, alternateResidence=DOMICILE
//
// The BE schema also requires a legacy `headquartersAddress` JSONB; the
// submit mapper derives it from `primary` so the wire stays compatible.

const AddressEntrySchema = z.object({
  street: z.string().max(200),
  city: z.string().max(120),
  province: z.string().max(10),
  zip: z.string().max(20),
  country: z.string().max(2),
});

export type AddressEntry = z.infer<typeof AddressEntrySchema>;

const ToggleableAddressSchema = z.object({
  enabled: z.boolean(),
  data: AddressEntrySchema,
});

export type ToggleableAddress = z.infer<typeof ToggleableAddressSchema>;

const OrganizationAddressesSchema = z.object({
  /**
   * PG → sede legale (REGISTERED_OFFICE). PF → residenza/abitazione
   * (RESIDENCE). Always required for the active legalNature.
   */
  primary: AddressEntrySchema,
  /** PG only — sede operativa diversa dalla sede legale. */
  alternateOffice: ToggleableAddressSchema,
  /** PG only — indirizzo di corrispondenza diverso. */
  mailingAddress: ToggleableAddressSchema,
  /** PF only — domicilio diverso dalla residenza. */
  alternateResidence: ToggleableAddressSchema,
});

const OrganizationContactsSchema = z.object({
  email: z.string().email('Email non valida'),
  pec: z.string().email('PEC non valida'),
  /**
   * Phone (telefono fisso). Required for COMPANY; optional for PERSON.
   * Cross-field rule below.
   */
  phone: PhoneEntrySchema,
  /**
   * Mobile (cellulare) dell'organizzazione. Optional for COMPANY (the
   * signatory's mobile lives on adminUser.mobilePhone), required for
   * PERSON (org IS the person, this is the person's mobile).
   */
  mobile: PhoneEntrySchema,
});

// ── Form root + cross-field validation ──────────────────────────────────────

export const RegistrationFormSchema = z
  .object({
    organization: OrganizationSchema,
    adminUser: AdminSchema,
    addresses: OrganizationAddressesSchema,
    orgContacts: OrganizationContactsSchema,
    acceptanceConfirmed: z.boolean().refine((v) => v === true, {
      message: 'Conferma la correttezza dei dati per inviare la richiesta',
    }),
    turnstileToken: z.string().min(1, 'Verifica anti-abuso non completata'),
  })
  .superRefine((data, ctx) => {
    // ── Responsible's personal RUI — required for AGENZIA/BROKER COMPANY ──
    //    PR-ONBOARDING-COMPANY-RESPONSIBLE-RUI-1.
    const responsibleOrgType = data.organization.orgType;
    if (
      data.organization.legalNature === 'COMPANY' &&
      (responsibleOrgType === 'AGENZIA' || responsibleOrgType === 'BROKER')
    ) {
      if (
        !data.adminUser.ruiNumber ||
        data.adminUser.ruiNumber.trim() === ''
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['adminUser', 'ruiNumber'],
          message:
            'Numero RUI del responsabile obbligatorio per agenzie/broker società.',
        });
      }
      if (
        !data.adminUser.ruiRegistrationDate ||
        data.adminUser.ruiRegistrationDate.trim() === ''
      ) {
        ctx.addIssue({
          code: 'custom',
          path: ['adminUser', 'ruiRegistrationDate'],
          message: 'Data di iscrizione RUI del responsabile obbligatoria.',
        });
      }
    }

    // ── Address required-ness driven by legalNature + enabled flags ──
    //    Empty fields on an active slot → 400 with per-field path.
    const isPerson = data.organization.legalNature === 'PERSON';
    const labelPrimary = isPerson
      ? 'Indirizzo di residenza'
      : 'Sede legale';

    validateAddressFields(
      ctx,
      ['addresses', 'primary'],
      data.addresses.primary,
      labelPrimary,
    );

    if (!isPerson) {
      if (data.addresses.alternateOffice.enabled) {
        validateAddressFields(
          ctx,
          ['addresses', 'alternateOffice', 'data'],
          data.addresses.alternateOffice.data,
          'Sede operativa',
        );
      }
      if (data.addresses.mailingAddress.enabled) {
        validateAddressFields(
          ctx,
          ['addresses', 'mailingAddress', 'data'],
          data.addresses.mailingAddress.data,
          'Sede di corrispondenza',
        );
      }
    } else {
      if (data.addresses.alternateResidence.enabled) {
        validateAddressFields(
          ctx,
          ['addresses', 'alternateResidence', 'data'],
          data.addresses.alternateResidence.data,
          'Domicilio',
        );
      }
    }

    // ── Phone required-ness per legalNature ─────────────────────────────
    //    PG → org.phone (fisso) required, org.mobile optional, admin.mobilePhone required (signatory)
    //    PF → org.phone optional, org.mobile optional, admin.mobilePhone required (data persona = data accesso)
    //
    // adminUser.mobilePhone è SEMPRE required: il cellulare della
    // persona che avrà l'utenza (login) è dato d'accesso, non
    // marketing. Per PF coincide con il cellulare della persona; per
    // PG con il cellulare del firmatario.
    const isPersonNature = data.organization.legalNature === 'PERSON';
    const orgPhoneFilled = data.orgContacts.phone.number.trim().length > 0;
    const adminMobileFilled =
      data.adminUser.mobilePhone.number.trim().length > 0;

    if (!isPersonNature && !orgPhoneFilled) {
      ctx.addIssue({
        code: 'custom',
        path: ['orgContacts', 'phone', 'number'],
        message: 'Telefono fisso richiesto per società',
      });
    }
    if (!adminMobileFilled) {
      ctx.addIssue({
        code: 'custom',
        path: ['adminUser', 'mobilePhone', 'number'],
        message: isPersonNature
          ? 'Cellulare richiesto'
          : 'Cellulare del firmatario richiesto',
      });
    }

    // ── PERSON identity coupling (mirror BE) ────────────────────────────
    if (data.organization.legalNature !== 'PERSON') return;

    const orgCf = data.organization.fiscalCode?.trim().toUpperCase() ?? '';
    const adminCf = data.adminUser.fiscalCode.trim().toUpperCase();
    if (orgCf.length > 0 && adminCf.length > 0 && orgCf !== adminCf) {
      ctx.addIssue({
        code: 'custom',
        path: ['adminUser', 'fiscalCode'],
        message:
          'Persona fisica: il codice fiscale deve coincidere con quello dell\'organizzazione',
      });
    }

    const orgName = normalizePersonalName(data.organization.legalName);
    const adminName = normalizePersonalName(data.adminUser.fullName);
    if (orgName.length > 0 && adminName.length > 0 && orgName !== adminName) {
      ctx.addIssue({
        code: 'custom',
        path: ['adminUser', 'fullName'],
        message:
          'Persona fisica: nome amministratore deve coincidere con quello dell\'organizzazione',
      });
    }
  });

function validateAddressFields(
  ctx: z.RefinementCtx,
  pathPrefix: ReadonlyArray<string | number>,
  entry: AddressEntry,
  // Used in the message so the user knows which slot failed when the
  // path is buried in `addresses.alternateOffice.data.street`.
  slotLabel: string,
): void {
  if (entry.street.trim().length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'street'],
      message: `${slotLabel}: via e civico richiesti`,
    });
  }
  if (entry.city.trim().length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'city'],
      message: `${slotLabel}: città richiesta`,
    });
  }
  if (entry.province.trim().length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'province'],
      message: `${slotLabel}: provincia richiesta`,
    });
  }
  if (entry.zip.trim().length === 0) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'zip'],
      message: `${slotLabel}: CAP richiesto`,
    });
  }
  if (!ISO_COUNTRY_REGEX.test(entry.country)) {
    ctx.addIssue({
      code: 'custom',
      path: [...pathPrefix, 'country'],
      message: `${slotLabel}: paese richiesto`,
    });
  }
}

export type RegistrationFormValues = z.infer<typeof RegistrationFormSchema>;

// Field paths grouped per step — used for partial validation when the user
// clicks "Continua".
//
// PR-C field paths are intentionally broad on the address step: `addresses`
// alone triggers validation on every slot (primary + every enabled
// alternate). RHF resolves the prefix and runs the resolver, which means
// the cross-field rules at the form root fire and surface per-field errors
// at the right paths — there's no need to enumerate every leaf.
export const STEP_FIELDS = {
  organization: [
    'organization.legalNature',
    'organization.legalName',
    'organization.orgType',
    'organization.fiscalCode',
    'organization.vatNumber',
    'organization.businessLegalForm',
    'organization.ruiNumber',
    'organization.ruiRegistrationDate',
  ],
  contacts: [
    'orgContacts.email',
    'orgContacts.pec',
    'orgContacts.mobile',
    'orgContacts.phone',
    // Trigger the form-root superRefine so per-legalNature required-ness
    // surfaces inline errors when the user clicks Continua without filling
    // the required phone slot for their flow.
    'orgContacts',
  ],
  address: ['addresses'],
  admin: [
    'adminUser.fullName',
    'adminUser.email',
    'adminUser.fiscalCode',
    'adminUser.sex',
    'adminUser.dateOfBirth',
    'adminUser.birthCountry',
    'adminUser.birthMunicipalityCadastralCode',
    'adminUser.placeOfBirth',
    'adminUser.mobilePhone',
    'adminUser.ruiNumber',
    'adminUser.ruiRegistrationDate',
  ],
  fiscal: ['organization.withholdingType'],
  summary: ['acceptanceConfirmed', 'turnstileToken'],
} as const satisfies Record<string, ReadonlyArray<string>>;

const EMPTY_ADDRESS: AddressEntry = {
  street: '',
  city: '',
  province: '',
  zip: '',
  country: 'IT',
};

// Exported for the resumable-area draft hydration (PR-ONB-RESUME-1B):
// server drafts are deep-merged OVER these defaults so missing keys never
// leave controlled inputs without a value.
export const DEFAULT_VALUES: RegistrationFormValues = {
  organization: {
    legalNature: 'PERSON',
    legalName: '',
    orgType: 'INTERMEDIARIO',
    fiscalCode: '',
    vatNumber: '',
    businessLegalForm: '',
    ruiNumber: '',
    ruiRegistrationDate: '',
    sdiCode: '',
    withholdingType: '',
  },
  // ⚠️ Several adminUser defaults below are intentionally NON-VALID
  //    (empty enum / empty date). RHF holds them so controlled inputs
  //    work, but `trigger(STEP_FIELDS.admin)` refuses to advance until
  //    the user fills them.
  adminUser: {
    fullName: '',
    email: '',
    fiscalCode: '',
    sex: '' as unknown as Sex,
    dateOfBirth: '',
    birthCountry: 'IT',
    birthMunicipalityCadastralCode: '',
    placeOfBirth: '',
    mobilePhone: { prefixIso: 'IT', number: '' },
    ruiNumber: '',
    ruiRegistrationDate: '',
  },
  addresses: {
    primary: { ...EMPTY_ADDRESS },
    alternateOffice: { enabled: false, data: { ...EMPTY_ADDRESS } },
    mailingAddress: { enabled: false, data: { ...EMPTY_ADDRESS } },
    alternateResidence: { enabled: false, data: { ...EMPTY_ADDRESS } },
  },
  orgContacts: {
    email: '',
    pec: '',
    mobile: { prefixIso: 'IT', number: '' },
    phone: { prefixIso: 'IT', number: '' },
  },
  acceptanceConfirmed: false,
  turnstileToken: '',
};

export function useRegistrationForm(): UseFormReturn<RegistrationFormValues> {
  return useForm<RegistrationFormValues>({
    resolver: zodResolver(RegistrationFormSchema),
    mode: 'onTouched',
    defaultValues: DEFAULT_VALUES,
  });
}
